import { useState } from "react";
import { Input } from "@/components/ui/input";
import { FormShell, FieldRow, submitForm, SubmitButton, DoneScreen } from "./FormShell";
import SignaturePad from "./SignaturePad";
import CountrySelect from "./CountrySelect";
import DateInput from "./DateInput";

const p = (k, d = "") => new URLSearchParams(window.location.search).get(k) || d;
const inp = "h-11 rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200";

export default function PublicCheckin() {
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [d, setD] = useState({
    vorname: p("vorname"),
    nachname: p("nachname"),
    strasse: "",
    plz_ort: "",
    land: p("land", "Deutschland"),
    staatsangehoerigkeit: p("staatsangehoerigkeit", "Deutschland"),
    ausweisnummer: "",
    telefon: p("telefon"),
    email: p("email"),
    geburtsdatum: "",
    anreise: p("check_in"),
    abreise: p("check_out"),
    personen: Number(p("guests", "1")) || 1,
    gast2: "",
    gast3: "",
    gast4: "",
    signature: null,
    einwilligung: false,
  });
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));

  // "personen" ist die Gesamtzahl inkl. Hauptgast — Gast 2 = zusätzliche Person, etc.
  const personen = Math.max(1, Math.min(4, Number(d.personen) || 1));
  const gast2Visible = personen >= 2;
  const gast3Visible = personen >= 3;
  const gast4Visible = personen >= 4;

  const submit = (e) => {
    e.preventDefault();
    if (!d.einwilligung) { alert("Bitte Datenschutz-Einwilligung bestätigen."); return; }
    if (!d.signature)   { alert("Bitte unterschreiben Sie das Formular."); return; }
    if (gast2Visible && !d.gast2.trim()) { alert("Bitte Namen für Gast 2 angeben."); return; }
    if (gast3Visible && !d.gast3.trim()) { alert("Bitte Namen für Gast 3 angeben."); return; }
    if (gast4Visible && !d.gast4.trim()) { alert("Bitte Namen für Gast 4 angeben."); return; }

    // Nur relevante Gastfelder mitschicken
    const payload = { ...d, personen };
    if (!gast2Visible) delete payload.gast2;
    if (!gast3Visible) delete payload.gast3;
    if (!gast4Visible) delete payload.gast4;

    submitForm({
      form_type: "checkin",
      data: payload,
      guest_name: `${d.vorname} ${d.nachname}`.trim(),
      setSaving,
      onDone: () => setDone(true),
    });
  };

  if (done) return <DoneScreen label="Ihre Meldedaten sind übermittelt. Wir freuen uns auf Ihren Aufenthalt." />;

  return (
    <FormShell title="Check-in Formular" subtitle="Digitale Gästemeldung">
      <form onSubmit={submit}>
        <FieldRow label="Vorname" required>
          <Input value={d.vorname} onChange={(e) => set("vorname", e.target.value)}
                 required className={inp} data-testid="checkin-vorname" />
        </FieldRow>
        <FieldRow label="Nachname" required>
          <Input value={d.nachname} onChange={(e) => set("nachname", e.target.value)}
                 required className={inp} data-testid="checkin-nachname" />
        </FieldRow>
        <FieldRow label="Straße und Hausnummer" required>
          <Input value={d.strasse} onChange={(e) => set("strasse", e.target.value)}
                 required className={inp} data-testid="checkin-strasse" />
        </FieldRow>
        <FieldRow label="PLZ und Ort" required>
          <Input value={d.plz_ort} onChange={(e) => set("plz_ort", e.target.value)}
                 required className={inp} data-testid="checkin-plz_ort" />
        </FieldRow>

        <FieldRow label="Land" required>
          <CountrySelect value={d.land} onChange={(v) => set("land", v)} required testid="checkin-land" />
        </FieldRow>
        <FieldRow label="Staatsangehörigkeit" required>
          <CountrySelect value={d.staatsangehoerigkeit} onChange={(v) => set("staatsangehoerigkeit", v)} required testid="checkin-staatsangehoerigkeit" />
        </FieldRow>

        <FieldRow label="Pass- oder Personalausweisnummer" required>
          <Input value={d.ausweisnummer} onChange={(e) => set("ausweisnummer", e.target.value)}
                 required className={inp} data-testid="checkin-ausweisnummer" />
        </FieldRow>

        {/* Kontaktdaten – untereinander, beide optional */}
        <FieldRow label="Telefonnummer">
          <Input type="tel" value={d.telefon} onChange={(e) => set("telefon", e.target.value)}
                 placeholder="z. B. +49 170 1234567" className={inp} data-testid="checkin-telefon" />
        </FieldRow>
        <FieldRow label="E-Mail">
          <Input type="email" value={d.email} onChange={(e) => set("email", e.target.value)}
                 placeholder="z. B. name@example.de" className={inp} data-testid="checkin-email" />
        </FieldRow>

        {/* Datum: Tastatureingabe + Kalender */}
        <FieldRow label="Geburtsdatum">
          <DateInput value={d.geburtsdatum} onChange={(v) => set("geburtsdatum", v)} testid="checkin-geburtsdatum" />
        </FieldRow>
        <FieldRow label="Anreise" required>
          <DateInput value={d.anreise} onChange={(v) => set("anreise", v)} required testid="checkin-anreise" />
        </FieldRow>
        <FieldRow label="Abreise" required>
          <DateInput value={d.abreise} onChange={(v) => set("abreise", v)} required testid="checkin-abreise" />
        </FieldRow>

        {/* Personenzahl steuert die Gast-Felder */}
        <FieldRow label="Anzahl der Personen" required>
          <select
            value={personen}
            onChange={(e) => set("personen", Number(e.target.value))}
            required
            data-testid="checkin-personen"
            className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 focus:outline-none"
          >
            <option value={1}>1 Person</option>
            <option value={2}>2 Personen</option>
            <option value={3}>3 Personen</option>
            <option value={4}>4 Personen</option>
          </select>
        </FieldRow>

        {gast2Visible && (
          <FieldRow label="Gast 2" required>
            <Input value={d.gast2} onChange={(e) => set("gast2", e.target.value)}
                   required placeholder="Vor- und Nachname" className={inp} data-testid="checkin-gast2" />
          </FieldRow>
        )}
        {gast3Visible && (
          <FieldRow label="Gast 3" required>
            <Input value={d.gast3} onChange={(e) => set("gast3", e.target.value)}
                   required placeholder="Vor- und Nachname" className={inp} data-testid="checkin-gast3" />
          </FieldRow>
        )}
        {gast4Visible && (
          <FieldRow label="Gast 4" required>
            <Input value={d.gast4} onChange={(e) => set("gast4", e.target.value)}
                   required placeholder="Vor- und Nachname" className={inp} data-testid="checkin-gast4" />
          </FieldRow>
        )}

        <FieldRow label="Datenschutzrechtliche Einwilligung" required>
          <label className="flex items-start gap-3 p-3 rounded-lg bg-indigo-50 border border-indigo-100 text-sm cursor-pointer">
            <input type="checkbox" checked={d.einwilligung}
                   onChange={(e) => set("einwilligung", e.target.checked)}
                   className="mt-0.5 h-4 w-4 accent-indigo-600"
                   data-testid="checkin-consent" />
            <span className="text-slate-700">
              Ich willige in die Verarbeitung meiner freiwilligen Angaben gemäß Datenschutzbestimmungen ein.
            </span>
          </label>
        </FieldRow>

        <FieldRow label="Unterschrift des Gastes" required>
          <SignaturePad onChange={(sig) => set("signature", sig)} testid="checkin-signature" />
        </FieldRow>

        <SubmitButton saving={saving} label="Check-in absenden" />
      </form>
    </FormShell>
  );
}
